import React, { useState, useEffect } from 'react';
import { generatedLettersService } from '../../services/letterService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './GeneratedLettersList.css';

const GeneratedLettersList = () => {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchLetters();
  }, []);

  const fetchLetters = async () => {
    try {
      setLoading(true);
      const fetchedLetters = await generatedLettersService.getAllGeneratedLetters();
      setLetters(fetchedLetters);
    } catch (error) {
      console.error('Error fetching letters:', error);
      alert('Failed to load letters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (letterId) => {
    if (!window.confirm('Are you sure you want to delete this letter?')) {
      return;
    }

    try {
      await generatedLettersService.deleteGeneratedLetter(letterId);
      await fetchLetters();
    } catch (error) {
      console.error('Error deleting letter:', error);
      alert('Failed to delete letter');
    }
  };

  const handlePreview = (letter) => {
    setSelectedLetter(letter);
    setShowPreview(true);
  };

  const handleDownloadPDF = async (letter) => {
    try {
      // Create a temporary element for PDF generation
      const tempElement = document.createElement('div');
      tempElement.style.position = 'absolute';
      tempElement.style.left = '-9999px';
      tempElement.style.top = '-9999px';
      tempElement.style.width = '210mm';
      tempElement.style.padding = '40px';
      tempElement.style.backgroundColor = 'white';
      tempElement.style.fontFamily = 'Times New Roman, serif';
      tempElement.style.lineHeight = '1.6';
      tempElement.style.color = '#333';
      
      tempElement.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #333;">
          <div>
            <h2 style="margin: 0; font-size: 24px; font-weight: bold;">GREY OFFICIAL</h2>
            <p style="margin: 5px 0 0 0; color: #666; font-size: 14px;">Official Document</p>
          </div>
          <div style="color: #333; font-size: 14px;">
            Date: ${new Date(letter.generatedAt).toLocaleDateString()}
          </div>
        </div>
        <div style="margin-bottom: 40px;">
          ${letter.content.split('\n').map(paragraph => `<p style="margin-bottom: 15px; text-align: justify;">${paragraph}</p>`).join('')}
        </div>
        <div style="margin-top: 40px;">
          <p style="margin-bottom: 10px;">Sincerely,</p>
          <p style="margin-bottom: 10px;">Grey Official Management</p>
        </div>
      `;
      
      document.body.appendChild(tempElement);
      
      const canvas = await html2canvas(tempElement, {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        backgroundColor: 'white'
      });
      
      document.body.removeChild(tempElement);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const filename = `${letter.templateName}_${new Date(letter.generatedAt).toLocaleDateString()}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="generated-letters-list">
        <div className="loading">Loading letters...</div>
      </div>
    );
  }

  return (
    <div className="generated-letters-list">
      <div className="header">
        <h1>Generated Letters</h1>
        <span className="letters-count">{letters.length} letters</span>
      </div>

      {letters.length === 0 ? (
        <div className="empty-state">
          <p>No letters generated yet. Use the Letter Generator to create your first letter.</p>
        </div>
      ) : (
        <div className="letters-grid">
          {letters.map(letter => (
            <div key={letter.id} className="letter-card">
              <div className="letter-header">
                <h3>{letter.templateName}</h3>
                <span className="letter-date">{formatDate(letter.generatedAt)}</span>
              </div>
              
              <div className="letter-preview">
                <p>{letter.content.substring(0, 150)}...</p>
              </div>
              
              <div className="letter-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => handlePreview(letter)}
                >
                  Preview
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => handleDownloadPDF(letter)}
                >
                  Download PDF
                </button>
                <button 
                  className="btn btn-danger"
                  onClick={() => handleDelete(letter.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && selectedLetter && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Letter Preview</h2>
              <button className="modal-close" onClick={() => setShowPreview(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="letter-content">
                <div className="letter-header-content">
                  <div className="letterhead">
                    <h2>GREY OFFICIAL</h2>
                    <p>Official Document</p>
                  </div>
                  <div className="letter-date">
                    Date: {new Date(selectedLetter.generatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="letter-body">
                  {selectedLetter.content.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                <div className="letter-footer">
                  <p>Sincerely,</p>
                  <p>Grey Official Management</p>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-primary"
                onClick={() => handleDownloadPDF(selectedLetter)}
              >
                Download PDF
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneratedLettersList;